import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import './App.css';

// Components
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import TimesheetList from './components/Timesheet/TimesheetList';
import TimesheetForm from './components/Timesheet/TimesheetForm';
import LeaveList from './components/Leave/LeaveList';
import LeaveForm from './components/Leave/LeaveForm';
import DocumentList from './components/Documents/DocumentList';
import ReviewList from './components/PerformanceReview/ReviewList';
import ReviewDetail from './components/PerformanceReview/ReviewDetail';
import ReviewForm from './components/PerformanceReview/ReviewForm';
import PersonalDetails from './components/PersonalDetails/PersonalDetails';
import VisaManagement from './components/VisaManagement/VisaManagement';
import HierarchyView from './components/Hierarchy/HierarchyView';
import NotificationList from './components/Notifications/NotificationList';
import EmployeeList from './components/Admin/EmployeeList';
import EmployeeForm from './components/Admin/EmployeeForm';
import DepartmentManagement from './components/Admin/DepartmentManagement';
import LeaveTypeManagement from './components/Admin/LeaveTypeManagement';
import Reports from './components/Reports/Reports';
import LeaveAllocationManagement from './components/Admin/LeaveAllocationManagement';
import ExEmployees from './components/Admin/ExEmployees';

// Protected Route Component (blocks ex-employees)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isExEmployee, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (isExEmployee) {
    return <Navigate to="/ex/personal-details" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

// Ex-Employee Route Component (read-only access)
const ExEmployeeRoute = ({ children }) => {
  const { isAuthenticated, isExEmployee, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isExEmployee) {
    return <Navigate to="/dashboard" replace />;
  }

  return <Layout>{children}</Layout>;
};

const App = () => {
  const { isAuthenticated, isExEmployee, loading } = useAuth();

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Auth Routes */}
      <Route
        path="/login"
        element={
          isAuthenticated
            ? <Navigate to={isExEmployee ? '/ex/personal-details' : '/dashboard'} replace />
            : <Login />
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Timesheet Routes */}
      <Route
        path="/timesheet"
        element={
          <ProtectedRoute>
            <TimesheetList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/timesheet/:date"
        element={
          <ProtectedRoute>
            <TimesheetForm />
          </ProtectedRoute>
        }
      />

      {/* Leave Routes */}
      <Route
        path="/leave"
        element={
          <ProtectedRoute>
            <LeaveList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leave/request"
        element={
          <ProtectedRoute>
            <LeaveForm />
          </ProtectedRoute>
        }
      />

      {/* Document Routes */}
      <Route
        path="/documents"
        element={
          <ProtectedRoute>
            <DocumentList />
          </ProtectedRoute>
        }
      />

      {/* Performance Review Routes */}
      <Route
        path="/performance-review"
        element={
          <ProtectedRoute>
            <ReviewList />
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance-review/new"
        element={
          <ProtectedRoute>
            <ReviewForm />
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance-review/:reviewId"
        element={
          <ProtectedRoute>
            <ReviewDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance-review/:reviewId/edit"
        element={
          <ProtectedRoute>
            <ReviewForm />
          </ProtectedRoute>
        }
      />

      {/* Personal Routes */}
      <Route
        path="/personal-details"
        element={
          <ProtectedRoute>
            <PersonalDetails />
          </ProtectedRoute>
        }
      />

      {/* Visa Routes */}
      <Route
        path="/visa"
        element={
          <ProtectedRoute>
            <VisaManagement />
          </ProtectedRoute>
        }
      />

      {/* Hierarchy Routes */}
      <Route
        path="/hierarchy"
        element={
          <ProtectedRoute>
            <HierarchyView />
          </ProtectedRoute>
        }
      />

      {/* Notification Routes */}
      <Route
        path="/notifications"
        element={
          <ProtectedRoute>
            <NotificationList />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/employees"
        element={
          <AdminRoute>
            <EmployeeList />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/employees/new"
        element={
          <AdminRoute>
            <EmployeeForm />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/employees/:id"
        element={
          <AdminRoute>
            <EmployeeForm />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/departments"
        element={
          <AdminRoute>
            <DepartmentManagement />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/leave-types"
        element={
          <AdminRoute>
            <LeaveTypeManagement />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/leave-allocations"
        element={
          <AdminRoute>
            <LeaveAllocationManagement />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/reports"
        element={
          <AdminRoute>
            <Reports />
          </AdminRoute>
        }
      />
      <Route
        path="/admin/ex-employees"
        element={
          <AdminRoute>
            <ExEmployees />
          </AdminRoute>
        }
      />

      {/* Ex-Employee Routes (Read-Only) */}
      <Route
        path="/ex/personal-details"
        element={
          <ExEmployeeRoute>
            <PersonalDetails />
          </ExEmployeeRoute>
        }
      />
      <Route
        path="/ex/timesheets"
        element={
          <ExEmployeeRoute>
            <TimesheetList />
          </ExEmployeeRoute>
        }
      />
      <Route
        path="/ex/leaves"
        element={
          <ExEmployeeRoute>
            <LeaveList />
          </ExEmployeeRoute>
        }
      />
      <Route
        path="/ex/documents"
        element={
          <ExEmployeeRoute>
            <DocumentList />
          </ExEmployeeRoute>
        }
      />
      <Route
        path="/ex/performance"
        element={
          <ExEmployeeRoute>
            <ReviewList />
          </ExEmployeeRoute>
        }
      />
      <Route
        path="/ex/performance/:reviewId"
        element={
          <ExEmployeeRoute>
            <ReviewDetail />
          </ExEmployeeRoute>
        }
      />

      {/* Root Route */}
      <Route
        path="/"
        element={<Navigate to={isExEmployee ? '/ex/personal-details' : '/dashboard'} replace />}
      />

      {/* 404 Route */}
      <Route
        path="*"
        element={
          <div className="loading">
            <div className="card">
              <h1>404 - Page Not Found</h1>
              <p>The page you are looking for does not exist.</p>
            </div>
          </div>
        }
      />
    </Routes>
  );
};

export default App;
